import {
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { getReviewId } from 'query/review';
import { getCriteria } from 'query/review/Criteria';
import type { CriteriaResponse } from 'query/review/Criteria';

const criteriaQueryKey = () => ['eligibilityCriteria', getReviewId() || ''];

const POLL_INTERVAL_MS = 60_000;
const STALE_TIME_MS = 60_000;

export type UseCriteriaQueryOptions = { poll?: boolean; enabled?: boolean };

// Reader: subscribes to the current review's criteria and keeps them fresh;
// enabled: false only observes what other subscribers fetch.
export function useCriteriaQuery(
  options?: UseCriteriaQueryOptions
): UseQueryResult<CriteriaResponse> {
  return useQuery({
    queryKey: criteriaQueryKey(),
    queryFn: () => getCriteria(),
    staleTime: STALE_TIME_MS,
    refetchInterval: options?.poll ? POLL_INTERVAL_MS : undefined,
    enabled: options?.enabled,
  });
}

type UseCriteriaCacheResult = {
  invalidate: () => void;
  getSnapshot: () => CriteriaResponse | undefined;
  setData: (response: CriteriaResponse) => Promise<void>;
};

// Writer: reads or replaces the cached criteria without subscribing to them.
export function useCriteriaCache(): UseCriteriaCacheResult {
  const queryClient = useQueryClient();

  return {
    invalidate: () => {
      queryClient.invalidateQueries({ queryKey: criteriaQueryKey() });
    },
    getSnapshot: () =>
      queryClient.getQueryData<CriteriaResponse>(criteriaQueryKey()),
    // Cancel in-flight fetches first: one started before the write could
    // settle late and overwrite the newer snapshot.
    setData: async (response: CriteriaResponse) => {
      const queryKey = criteriaQueryKey();
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<CriteriaResponse>(queryKey, response);
    },
  };
}
